import React, { FormEvent, useCallback, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'

import Text from '../../../ui/Text'
import { LandingPageLayout, SContentWrapper, SSpacer } from '../../../lib/landing-page-layout'
import { useAuthRequestPasswordResetMutation } from '../../redux/api/web-api'
import { getErrorMessageCode } from '../../utils/getErrorMessageCode'
import { validateEmailFormat } from '../../utils/form-validation/field-format-validation'
import { InputGroup, SInput } from '../../../lib/input-group'
import { FormHeader } from '../../../lib/form-header'
import { SubmitButton } from '../../../lib/submit-button'
import { theme } from '../../../lib/theme'
import useText from '../../hooks/useText'

const SFormWrapper = styled(SContentWrapper)`
    max-width: 510px;
    padding: 0 10px;
`

const SLink = styled.a`
    &,
    &:hover,
    &:active,
    &:visited,
    &:focus {
        text-decoration: none;
        color: ${({ theme }) => theme.font.midDarkColor};
        text-align: center;
        width: 100%;
        display: inline-block;
        padding-top: 30px;
    }
`

const SInstruction = styled.div`
    line-height: 1.5em;
    padding-bottom: 37px;
    text-align: center;
`

const SHeaderWrapper = styled.div`
    padding-bottom: 45px;
`

export type ResetPasswordRequestPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export function ResetPasswordRequestPage({ className, style }: ResetPasswordRequestPageProps): React.ReactElement {
    const text = useText()
    const [email, setEmail] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigate()

    const [requestPasswordReset, { isLoading }] = useAuthRequestPasswordResetMutation()

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (!validateEmailFormat(email)) {
                setErrorMessage(text('msgEmailFormatIncorrect'))
                return
            }

            requestPasswordReset({ userCredentials: { email } })
                .unwrap()
                .then(() => {
                    navigate('/reset-password-request-success', { replace: true })
                })
                .catch((error) => {
                    if (getErrorMessageCode(error) === 'msgEmailDoesntExist') {
                        setErrorMessage(text('msgEmailDoesntExist', { company: 'ChargeAmps' }))
                        return
                    }
                    navigate('/reset-password-request-error', { replace: true })
                })
        },
        [email, navigate, requestPasswordReset, text]
    )
    return (
        <LandingPageLayout
            className={cn(className)}
            style={style}
            background={theme.whiteBackground}
            hideLanguageSelect
        >
            <SFormWrapper>
                <SHeaderWrapper>
                    <FormHeader data-testid="header" />
                </SHeaderWrapper>
                <SInstruction data-testid="description">
                    <Text code="msgChargeAmpsResetPasswordInstruction" />
                </SInstruction>
                <form onSubmit={handleSubmit} data-testid="form" noValidate>
                    <SSpacer>
                        <InputGroup
                            errorTestId="errorMsg"
                            errorMsg={errorMessage}
                            inverted={false}
                            label={email && text('lblEmail')}
                        >
                            <SInput
                                placeholder={text('lblEmail')}
                                onInput={(event) => {
                                    const value = (event.target as HTMLInputElement).value
                                    setErrorMessage('')
                                    setEmail(value)
                                }}
                                value={email}
                                data-testid="emailInput"
                            />
                        </InputGroup>
                    </SSpacer>
                    <SubmitButton disabled={isLoading}>
                        <Text code="btnResetPassword" />
                    </SubmitButton>
                </form>
                <SLink as={Link} to="/login" data-testid="returnBtn">
                    <Text code="btnReturnToSignIn" />
                </SLink>
            </SFormWrapper>
        </LandingPageLayout>
    )
}
